import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { updateCustomerProfile } from '../services/firebase';
import 'leaflet/dist/leaflet.css';

const DEFAULT_LAT = 50.8825;
const DEFAULT_LNG = 6.8647;

const CompleteProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang } = useLanguage();
  const returnTo = (location.state as any)?.from || '/';

  const session = (() => {
    try { return JSON.parse(localStorage.getItem('sushiki_session') || '{}'); } catch { return {}; }
  })();

  const [name, setName] = useState(session.profile?.name || '');
  const [street, setStreet] = useState(session.profile?.street || '');
  const [postcode, setPostcode] = useState(session.profile?.postcode || '');
  const [city, setCity] = useState(session.profile?.city || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Address search
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimer = useRef<NodeJS.Timeout | null>(null);

  // Map
  const [lat, setLat] = useState(DEFAULT_LAT);
  const [lng, setLng] = useState(DEFAULT_LNG);
  const [mapError, setMapError] = useState('');
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<any>(null);
  const markerRef = useRef<any>(null);

  // Branded SushiKi marker icon as SVG div
  const createBrandedIcon = useCallback((L: any) => {
    return L.divIcon({
      html: `<div style="
        width:44px;height:54px;position:relative;display:flex;flex-direction:column;align-items:center;
      ">
        <div style="
          width:40px;height:40px;border-radius:50%;
          background:#1a1d2e;border:3px solid #fff;
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 3px 12px rgba(0,0,0,0.45);
          position:relative;z-index:2;
        ">
          <img src="/sushiki_logo_w.png" style="width:28px;height:28px;border-radius:50%;object-fit:contain;" onerror="this.style.display='none'" />
        </div>
        <div style="
          width:0;height:0;
          border-left:8px solid transparent;
          border-right:8px solid transparent;
          border-top:12px solid #1a1d2e;
          margin-top:-2px;
          filter:drop-shadow(0 2px 3px rgba(0,0,0,0.2));
        "></div>
        <div style="
          width:14px;height:4px;border-radius:50%;
          background:rgba(0,0,0,0.15);
          margin-top:1px;
        "></div>
      </div>`,
      className: '',
      iconSize: [44, 60],
      iconAnchor: [22, 58],
      popupAnchor: [0, -58],
    });
  }, []);

  // Load map
  useEffect(() => {
    if (!mapRef.current || leafletMap.current) return;

    let resizeObserver: ResizeObserver | null = null;

    const loadMap = async () => {
      try {
        const L = (await import('leaflet')).default;

        const map = L.map(mapRef.current!, {
          center: [lat, lng], zoom: 14, zoomControl: true,
        });

        L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
          attribution: '&copy; OSM &copy; CARTO', maxZoom: 19,
        }).addTo(map);

        const icon = createBrandedIcon(L);
        const marker = L.marker([lat, lng], { draggable: true, icon });
        marker.addTo(map);

        marker.on('dragend', () => {
          const pos = marker.getLatLng();
          setLat(pos.lat); setLng(pos.lng);
          reverseGeocode(pos.lat, pos.lng);
        });

        map.on('click', (e: any) => {
          marker.setLatLng(e.latlng);
          setLat(e.latlng.lat); setLng(e.latlng.lng);
          reverseGeocode(e.latlng.lat, e.latlng.lng);
        });

        leafletMap.current = map;
        markerRef.current = marker;

        // Fix mobile rendering: container size may not be final when map inits.
        // invalidateSize forces Leaflet to recompute tile dimensions.
        const refresh = () => { try { map.invalidateSize(); } catch {} };
        setTimeout(refresh, 50);
        setTimeout(refresh, 300);
        setTimeout(refresh, 800);
        if (typeof ResizeObserver !== 'undefined' && mapRef.current) {
          resizeObserver = new ResizeObserver(refresh);
          resizeObserver.observe(mapRef.current);
        }
        window.addEventListener('orientationchange', refresh);
        window.addEventListener('resize', refresh);

        // GPS auto-detect
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const userLat = pos.coords.latitude;
              const userLng = pos.coords.longitude;
              setLat(userLat); setLng(userLng);
              map.setView([userLat, userLng], 16);
              marker.setLatLng([userLat, userLng]);
              reverseGeocode(userLat, userLng);
            },
            () => {}
          );
        }
      } catch (e: any) {
        setMapError(e?.message || 'Map failed to load');
      }
    };

    loadMap();
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
  }, []); // eslint-disable-line

  // Reverse geocode — fills all fields + search box
  const reverseGeocode = async (latitude: number, longitude: number) => {
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
        { headers: { 'User-Agent': 'SushiKiApp/1.0' } }
      );
      const data = await resp.json();
      if (data.address) {
        const a = data.address;
        const streetName = a.road || a.pedestrian || a.footway || '';
        const houseNum = a.house_number || '';
        const fullStreet = (houseNum ? `${streetName} ${houseNum}` : streetName).trim();
        const fullCity = a.city || a.town || a.village || a.state || '';
        const fullPostcode = a.postcode || '';
        setStreet(fullStreet);
        setPostcode(fullPostcode);
        setCity(fullCity);
        setSearchQuery(data.display_name || `${fullStreet}, ${fullPostcode} ${fullCity}`);
      }
    } catch {}
  };

  // Address autocomplete — worldwide, no country restriction
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }

    searchTimer.current = setTimeout(async () => {
      try {
        const resp = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=5&addressdetails=1`,
          { headers: { 'User-Agent': 'SushiKiApp/1.0' } }
        );
        const data = await resp.json();
        setSuggestions(data || []);
        setShowSuggestions(data.length > 0);
      } catch { setSuggestions([]); }
    }, 400);
  };

  // Select suggestion — fills search box + all fields + moves pin
  const selectSuggestion = (suggestion: any) => {
    const a = suggestion.address || {};
    const newLat = parseFloat(suggestion.lat);
    const newLng = parseFloat(suggestion.lon);

    const streetName = a.road || a.pedestrian || a.footway || '';
    const houseNum = a.house_number || '';
    const fullStreet = (houseNum ? `${streetName} ${houseNum}` : streetName).trim();
    const fullCity = a.city || a.town || a.village || a.state || '';
    setStreet(fullStreet);
    setPostcode(a.postcode || '');
    setCity(fullCity);
    setSearchQuery(suggestion.display_name || `${fullStreet}, ${a.postcode || ''} ${fullCity}`);
    setLat(newLat); setLng(newLng);
    setSuggestions([]);
    setShowSuggestions(false);

    if (leafletMap.current && markerRef.current) {
      leafletMap.current.setView([newLat, newLng], 17);
      markerRef.current.setLatLng([newLat, newLng]);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError(lang === 'de' ? 'Name ist erforderlich' : 'Name is required');
      return;
    }
    setSaving(true); setError('');
    try {
      await updateCustomerProfile({
        sessionToken: session.sessionToken,
        name: name.trim(), street: street.trim(), postcode: postcode.trim(), city: city.trim(),
        latitude: lat, longitude: lng,
      });

      const updatedSession = {
        ...session,
        profile: { ...session.profile, name: name.trim(), street: street.trim(), postcode: postcode.trim(), city: city.trim() },
      };
      localStorage.setItem('sushiki_session', JSON.stringify(updatedSession));
      window.dispatchEvent(new Event('sushiki-auth-changed'));
      navigate(returnTo, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', borderRadius: 10,
    border: '1px solid #3a3d52', background: '#1e2130', color: '#fff',
    fontSize: '1rem', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div className="page" style={{ background: '#13151f', minHeight: '100vh' }}>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ color: '#fff', fontSize: '1.2rem', margin: 0 }}>
            {lang === 'de' ? 'Profil vervollständigen' : 'Complete Your Profile'}
          </h2>
          <div className="lang-toggle-landing" style={{ position: 'relative' }}>
            <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
            <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
          </div>
        </div>

        <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: 20 }}>
          {lang === 'de'
            ? 'Bitte geben Sie Ihren Namen und Ihre Lieferadresse ein.'
            : 'Please enter your name and delivery address.'}
        </p>

        {/* Name */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ color: '#bbb', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>
            {lang === 'de' ? 'Vollständiger Name' : 'Full Name'} *
          </label>
          <input type="text" value={name} placeholder="Max Mustermann" autoFocus
            onChange={(e) => setName(e.target.value)} style={inputStyle} />
        </div>

        {/* Phone (read-only) */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ color: '#bbb', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>
            {lang === 'de' ? 'Telefon' : 'Phone'}
          </label>
          <div style={{ ...inputStyle, background: '#1a1d2e', color: '#94e815' }}>
            {session.phone || '—'}
          </div>
        </div>

        {/* Address Search with Autocomplete */}
        <div style={{ marginBottom: 14, position: 'relative' }}>
          <label style={{ color: '#bbb', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>
            🔍 {lang === 'de' ? 'Adresse suchen' : 'Search address'}
          </label>
          <input
            type="text" value={searchQuery}
            placeholder={lang === 'de' ? 'z.B. Sudetenstraße 60, Hürth' : 'e.g. Sudetenstraße 60, Hürth'}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            style={inputStyle}
          />
          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
              background: '#1e2130', border: '1px solid #3a3d52', borderRadius: 10,
              marginTop: 4, maxHeight: 200, overflowY: 'auto',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            }}>
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  onClick={() => selectSuggestion(s)}
                  style={{
                    padding: '10px 14px', cursor: 'pointer',
                    borderBottom: i < suggestions.length - 1 ? '1px solid #2a2d42' : 'none',
                    fontSize: '0.85rem', color: '#e0e0e0',
                  }}
                  onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#2a2d42'; }}
                  onMouseLeave={(e) => { (e.target as HTMLElement).style.background = 'transparent'; }}
                >
                  <span style={{ color: '#94e815', marginRight: 6 }}>📍</span>
                  {s.display_name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Address fields (auto-filled from search or map) */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ color: '#bbb', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>
            {lang === 'de' ? 'Straße & Hausnummer' : 'Street & Number'}
          </label>
          <input type="text" value={street} placeholder="Musterstraße 12"
            onChange={(e) => setStreet(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ color: '#bbb', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>
              {lang === 'de' ? 'PLZ' : 'Postcode'}
            </label>
            <input type="text" value={postcode} placeholder="50354"
              onChange={(e) => setPostcode(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ color: '#bbb', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>
              {lang === 'de' ? 'Stadt' : 'City'}
            </label>
            <input type="text" value={city} placeholder="Hürth"
              onChange={(e) => setCity(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* Map */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ color: '#bbb', fontSize: '0.8rem', display: 'block', marginBottom: 4 }}>
            📍 {lang === 'de' ? 'Standort auf Karte bestätigen' : 'Confirm location on map'}
          </label>
          <p style={{ color: '#666', fontSize: '0.72rem', marginBottom: 6, marginTop: 0 }}>
            {lang === 'de'
              ? 'Tippen oder ziehen Sie den Pin zu Ihrem Standort.'
              : 'Tap or drag the pin to your exact location.'}
          </p>
          <div ref={mapRef} style={{
            width: '100%', height: 240, minHeight: 240, borderRadius: 12,
            border: '1px solid #3a3d52', overflow: 'hidden',
            background: '#2a2d3e',
          }} />
          {mapError && (
            <p style={{ color: '#ef5350', fontSize: '0.72rem', marginTop: 6 }}>
              {lang === 'de'
                ? 'Karte konnte nicht geladen werden. Adresse kann weiterhin manuell eingegeben werden.'
                : 'Map could not load. You can still enter the address manually.'}
            </p>
          )}
          <style>{`
            .leaflet-div-icon { background: transparent !important; border: none !important; }
            .leaflet-container { background: #2a2d3e; }
          `}</style>
        </div>

        {error && <p style={{ color: '#ef5350', fontSize: '0.8rem', marginBottom: 8 }}>{error}</p>}

        <button onClick={handleSave} disabled={saving || !name.trim()}
          style={{
            width: '100%', padding: '14px 0', borderRadius: 10,
            border: 'none', background: saving ? '#555' : '#94e815', color: '#1a1d2e',
            fontSize: '1rem', fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
            marginTop: 8, opacity: !name.trim() ? 0.5 : 1,
          }}>
          {saving ? (lang === 'de' ? 'Wird gespeichert...' : 'Saving...') : (lang === 'de' ? 'Speichern & weiter' : 'Save & Continue')}
        </button>

        <button onClick={() => navigate(returnTo, { replace: true })}
          style={{
            width: '100%', background: 'transparent', border: 'none',
            color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer',
            marginTop: 12, textDecoration: 'underline', padding: '8px 0',
          }}>
          {lang === 'de' ? 'Später ausfüllen' : 'Fill in later'}
        </button>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
